import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react'
import { getProfile, UserProfile } from '../api/profile'
import { firebaseAuthService, FirebaseUserData } from '../services/firebaseAuthService'
import { API_BASE_URL } from '../config/api'

interface UserContextType {
  user: UserProfile | null
  firebaseUser: FirebaseUserData | null
  isLoading: boolean
  isAuthenticated: boolean
  refreshUser: () => Promise<UserProfile | null>
  logout: () => Promise<void>
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export const useUser = () => {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}

interface UserProviderProps {
  children: ReactNode
}

function profileToFirebaseUser(profile: UserProfile, uid: string): FirebaseUserData {
  return {
    uid,
    email: profile.email,
    firstName: profile.firstName,
    lastName: profile.lastName,
    phone: profile.phone,
    userType: profile.userType as FirebaseUserData['userType'],
    profileImage: profile.profileImage,
    isVerified: profile.isVerified,
    onboardingCompleted: profile.onboardingCompleted,
    createdAt: profile.createdAt,
    backendUserId: profile.id,
  }
}

function isNetworkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  const message = error.message.toLowerCase()
  return (
    error.name === 'TypeError' ||
    message.includes('failed to fetch') ||
    message.includes('network') ||
    message.includes('load failed')
  )
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUserData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const userRef = useRef<UserProfile | null>(null)
  const refreshPromiseRef = useRef<Promise<UserProfile | null> | null>(null)
  const didAttemptSyncRef = useRef(false)
  userRef.current = user

  const refreshUser = useCallback(async (): Promise<UserProfile | null> => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current
    }

    const run = (async (): Promise<UserProfile | null> => {
      try {
        setIsLoading(true)

        const jwtToken = localStorage.getItem('token')
        if (jwtToken) {
          try {
            const profile = await getProfile()
            if (profile) {
              setUser(profile)
              setFirebaseUser(null)
              return profile
            }
          } catch (error) {
            console.error('Error fetching profile with JWT token:', error)
            if (!isNetworkError(error)) {
              localStorage.removeItem('token')
            }
          }
        }

        const { auth } = await import('../config/firebase')
        const firebaseAuthUser = auth.currentUser
        if (!firebaseAuthUser) {
          setFirebaseUser(null)
          setUser(null)
          localStorage.removeItem('firebaseToken')
          return null
        }

        const registrationInProgress = sessionStorage.getItem('registrationInProgress')
        if (registrationInProgress) {
          for (let attempt = 0; attempt < 6; attempt++) {
            try {
              const profile = await getProfile(attempt > 0)
              if (profile) {
                setUser(profile)
                setFirebaseUser(profileToFirebaseUser(profile, firebaseAuthUser.uid))
                return profile
              }
            } catch {
              // Backend row may not exist yet
            }
            await new Promise((resolve) => setTimeout(resolve, 500))
          }
          console.warn('⚠️ Profile not ready during registration; skipping sync to avoid wrong userType')
          return userRef.current
        }

        let profile: UserProfile | null = null
        try {
          profile = await getProfile(false)
        } catch (error) {
          console.error('Error fetching backend profile:', error)
          if (isNetworkError(error)) {
            return userRef.current
          }
        }

        if (profile) {
          setUser(profile)
          setFirebaseUser(profileToFirebaseUser(profile, firebaseAuthUser.uid))
          return profile
        }

        if (didAttemptSyncRef.current) {
          return userRef.current
        }
        didAttemptSyncRef.current = true

        // Only attempt firebase-sync when the backend is reachable but has no user row.
        // Never sync on CORS/network failures, and never invent a userType for existing users.
        try {
          const syncToken = await firebaseAuthService.getFirebaseToken(false)
          if (!syncToken || !firebaseAuthUser.email) {
            return userRef.current
          }

          const syncResponse = await fetch(`${API_BASE_URL}/api/auth/firebase-sync`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${syncToken}`,
            },
            body: JSON.stringify({
              firebaseUid: firebaseAuthUser.uid,
              email: firebaseAuthUser.email,
              firstName: '',
              lastName: '',
              phone: '0000000000',
              userType: 'VOLUNTEER',
            }),
          })

          if (!syncResponse.ok) {
            return userRef.current
          }

          const syncData = await syncResponse.json()
          const synced: UserProfile = {
            id: syncData.data.id,
            email: syncData.data.email,
            firstName: syncData.data.firstName,
            lastName: syncData.data.lastName,
            phone: syncData.data.phone,
            userType: syncData.data.userType,
            isVerified: syncData.data.isVerified,
            onboardingCompleted: syncData.data.onboardingCompleted ?? false,
            createdAt: syncData.data.createdAt,
          }
          setUser(synced)
          setFirebaseUser(profileToFirebaseUser(synced, firebaseAuthUser.uid))
          return synced
        } catch (syncError) {
          console.error('Error syncing user with backend:', syncError)
          return userRef.current
        }
      } catch (error) {
        console.error('Error refreshing user:', error)
        if (!isNetworkError(error) && !userRef.current) {
          setUser(null)
          setFirebaseUser(null)
        }
        return userRef.current
      } finally {
        setIsLoading(false)
      }
    })()

    refreshPromiseRef.current = run
    try {
      return await run
    } finally {
      refreshPromiseRef.current = null
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      didAttemptSyncRef.current = false
      await firebaseAuthService.signOut()
      setUser(null)
      setFirebaseUser(null)
      localStorage.removeItem('firebaseToken')
      localStorage.removeItem('token')
    } catch (error) {
      console.error('Error during logout:', error)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const { auth } = await import('../config/firebase')
      await auth.authStateReady()
      if (!cancelled) {
        await refreshUser()
      }
    })()
    return () => {
      cancelled = true
    }
  }, [refreshUser])

  const isAuthenticated = !!user && (!!firebaseUser || !!localStorage.getItem('token'))

  const value: UserContextType = {
    user,
    firebaseUser,
    isLoading,
    isAuthenticated,
    refreshUser,
    logout,
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}
